import {
  BlankSlate,
  Box,
  Button,
  Icon,
  Link,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { ProjectionKind, useCompetenciesDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { CompetencyItemActions } from './CompetencyItemActions'
import { CompetenciesFilterOptions, competenciesFilters } from './Filters'
import { TableBulkActions } from './TableBulkActions'

interface Props {
  filterOptions: CompetenciesFilterOptions
  isCompetenciesRevampEnabled: boolean
  onNewCompetency: () => void
}

export const CompetenciesDataTable = ({
  filterOptions,
  isCompetenciesRevampEnabled,
  onNewCompetency,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const source = useCompetenciesDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Path,
        title: i18n.t('competencies_v3.competencies_table.columns.name'),
        path: 'name',
      },
      {
        identifier: 'audience',
        kind: ProjectionKind.Custom,
        title: i18n.t('competencies_v3.competencies_table.columns.audience'),
        template: '{{ everyone }}',
        hidden: isCompetenciesRevampEnabled,
        render: ({ everyone, roles }) => <Audience everyone={everyone} roles={roles} />,
      },
      {
        identifier: 'competency_group_name',
        kind: ProjectionKind.Custom,
        sortBy: 'competencyGroupId',
        title: i18n.t('competencies_v3.competencies_table.columns.group'),
        template: '{{ competencyGroup.name }}',
        render: ({ competencyGroup }) =>
          competencyGroup ? <Tag inline>{competencyGroup.name}</Tag> : '-',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: (competency) => (
          <CompetencyItemActions
            competency={competency}
            isCompetenciesRevampEnabled={isCompetenciesRevampEnabled}
          />
        ),
        width: 80,
      },
    ],
    {
      variables: { search: debouncedSearch },
    }
  )

  return (
    <DataTable
      source={source}
      selectable={() => true}
      disableDefaultActions
      sort={{ path: 'name', direction: 'asc' }}
      filters={competenciesFilters(filterOptions, isCompetenciesRevampEnabled)}
      enableClearFilters
      emptyState={
        debouncedSearch.length > 0 ? (
          <BlankSlate
            illustration="window-user"
            title={i18n.t('competencies_v3.competencies_table.no_results.title')}
            description={i18n.t('competencies_v3.competencies_table.no_results.description')}
          />
        ) : (
          <BlankSlate
            illustration="window-user"
            title={i18n.t('competencies_v3.competencies_table.empty_state.title')}
            description={i18n.t('competencies_v3.competencies_table.empty_state.description')}
          >
            <Box gap="s24" alignItems="center">
              <Box flexDirection="row" gap="s8">
                <Button onClick={() => onNewCompetency()}>
                  {i18n.t('competencies_v3.competencies_table.empty_state.create_button')}
                </Button>
              </Box>
              <Link.Href href="#" target="_blank">
                {i18n.t('competencies_v3.competencies_table.empty_state.help_link')}
              </Link.Href>
            </Box>
          </BlankSlate>
        )
      }
      actions={
        <>
          <Button onClick={() => onNewCompetency()} iconName="add">
            {i18n.t('competencies_v3.competencies_table.new_competency')}
          </Button>
          <TableBulkActions />
        </>
      }
      search={{
        placeholder: i18n.t('common.search_by'),
        onChange: onSearch,
        value: search,
      }}
    />
  )
}

interface AudienceProps {
  everyone: boolean
  roles: { name: string }[]
}

const audienceText = (everyone: boolean, rolesCount: number, roleName?: string) => {
  if (everyone) {
    return i18n.t('competencies_v3.competencies_table.audience.everyone')
  }
  if (rolesCount === 0) {
    return i18n.t('competencies_v3.competencies_table.audience.no_jobs')
  }
  if (rolesCount === 1) {
    return roleName
  }
  return i18n.t('competencies_v3.competencies_table.audience.many_jobs', { count: rolesCount })
}

const Audience = ({ everyone, roles }: AudienceProps) => (
  <Box gap="s4" flexDirection="row" alignItems="center">
    <Icon.Small icon={everyone ? 'office' : 'briefcase'} />
    <Text.Normal>{audienceText(everyone, roles.length, roles[0]?.name)}</Text.Normal>
  </Box>
)
